<template>
  <div>
    <div class="card">
      <div class="card-header py-2">
        <Spinner v-if="loading" margin_right="10px" height="20px" />
        <b-form-checkbox v-else class="display-inline marginright5" v-model="checkedAll" :disabled="!loaded"></b-form-checkbox>
        <span @click="loaded ? (checkedAll = !checkedAll) : null">Komoot Highlight Poi</span>
        <div style="float: right;">
          <img @click="expanded = !expanded" style="height: 13px; cursor: pointer;"
            :src="expanded ? icons.chevronUp : icons.chevronDown" />
        </div>
      </div>
      <div class="card-body" v-if="expanded">
        <b-form-checkbox class="display-inline marginright5" v-model="active"></b-form-checkbox>
        <span @click="active = !active">Active</span>
        <b-form-checkbox class="display-inline marginright5" v-model="inactive"></b-form-checkbox>
        <span @click="inactive = !inactive">Inactive</span>
      </div>
    </div>
    <br>
    <div class="card">
      <div class="card-header py-2">
        <Spinner v-if="loading" margin_right="10px" height="20px" />
        <b-form-checkbox v-else class="display-inline marginright5" v-model="checkedAllOther" :disabled="!loaded"></b-form-checkbox>
        <span @click="loaded ? (checkedAllOther = !checkedAllOther) : null">Komoot Other Poi</span>
        <div style="float: right;">
          <img @click="expandedOther = !expandedOther" style="height: 13px; cursor: pointer;"
            :src="expandedOther ? icons.chevronUp : icons.chevronDown" />
        </div>
      </div>
      <div class="card-body" v-if="expandedOther">
        <b-form-checkbox class="display-inline marginright5" v-model="activeOther"></b-form-checkbox>
        <span @click="activeOther = !activeOther">Active</span>
        <b-form-checkbox class="display-inline marginright5" v-model="inactiveOther"></b-form-checkbox>
        <span @click="inactiveOther = !inactiveOther">Inactive</span>
      </div>
    </div>
  </div>
</template>

<style scoped>
:global(.komootpoi-pointer-cursor),
:global(.komootpoi-pointer-cursor *) {
  cursor: pointer !important;
}
</style>

<script lang="ts">
import BaseComponent from './Base.vue';
import { EventBus } from '@/ts/EventBus';
import Photo from '@/ts/Photo';
import { AlertStatus, DownloadStatus } from '@/ts/types';
import { defineComponent } from "vue";
import L from 'leaflet';
import { DataFilterExtension } from '@deck.gl/extensions';
import { DeckOverlay } from '@deck.gl-community/leaflet';
import { MapLasScatterplotLayer } from '@/ts/MapLasScatterplotLayer';
import lightGallery from 'lightgallery';
import lgZoom from 'lightgallery/plugins/zoom';
import lgAutoplay from 'lightgallery/plugins/autoplay';

interface KomootPoiResponse {
  id: number;
  name: string;
  lat: number | string;
  lon: number | string;
  type: string;
  waypoints: string | null;
  photos: string[];
  is_active: boolean;
}

interface KomootPoiBinaryData {
  length: number;
  attributes: {
    getPosition: { value: Float32Array; size: number };
    getFillColor: { value: Uint8Array; size: 4 };
    getLineColor: { value: Uint8Array; size: 4 };
    getFilterValue: { value: Float32Array; size: 1 };
  };
}

interface KomootPoiPreparedData {
  data: KomootPoiBinaryData;
  results: KomootPoiResponse[];
}

interface KomootPoiPopupPlace {
  id: number;
  name: string;
  lat: number;
  lon: number;
  photos: Photo[];
}

interface KomootPoiCardInstanceState {
  komootPoiResult: KomootPoiResponse[] | null;
  highlightLayerResults: KomootPoiResponse[];
  otherLayerResults: KomootPoiResponse[];
  highlightLayer: MapLasScatterplotLayer | null;
  otherLayer: MapLasScatterplotLayer | null;
  deckOverlay: DeckOverlay | null;
  mapMouseMoveHandler: ((event: L.LeafletMouseEvent) => void) | null;
  mapMouseOutHandler: (() => void) | null;
  mapClickHandler: ((event: L.LeafletMouseEvent) => void) | null;
  hoveredWaypoints: L.Polyline[];
  hoveredPoiKey: string | null;
}

function isHighlightPoi(place: KomootPoiResponse): boolean {
  return place.type === 'highlight_point' || place.type === 'highlight_segment';
}

declare module 'vue' {
  interface ComponentCustomProperties extends KomootPoiCardInstanceState {}
}

export default defineComponent({
  extends: BaseComponent,
  data() {
    return {
      checkedAll: false,
      expanded: false,
      active: true,
      inactive: false,
      checkedAllOther: false,
      expandedOther: false,
      activeOther: true,
      inactiveOther: false,
      loaded: false,
      loading: false,
    };
  },
  created() {
    this.komootPoiResult = null;
    this.highlightLayerResults = [];
    this.otherLayerResults = [];
    this.highlightLayer = null;
    this.otherLayer = null;
    this.deckOverlay = null;
    this.mapMouseMoveHandler = null;
    this.mapMouseOutHandler = null;
    this.mapClickHandler = null;
    this.hoveredWaypoints = [];
    this.hoveredPoiKey = null;
    EventBus.$on('downloadExternalData', () => this.downloadPlacesKomootPoi());
    EventBus.$on('toggleKomootPoi', () => {
      if (this.komootPoiResult) {
        this.checkedAll = !this.checkedAll;
        this.checkedAllOther = !this.checkedAllOther;
      }
    });
    EventBus.$on('selectedAreaChanged', () => this.onSelectedAreaChanged());
  },
  beforeUnmount() {
    this.clearHoveredWaypoints();
    if (window.mainmap && this.mapMouseMoveHandler) {
      window.mainmap.off('mousemove', this.mapMouseMoveHandler);
    }
    if (window.mainmap && this.mapMouseOutHandler) {
      window.mainmap.off('mouseout', this.mapMouseOutHandler);
    }
    if (window.mainmap && this.mapClickHandler) {
      window.mainmap.off('click', this.mapClickHandler);
    }
    if (window.mainmap) {
      window.mainmap.getContainer().classList.remove('komootpoi-pointer-cursor');
      if (this.deckOverlay) window.mainmap.removeLayer(this.deckOverlay);
    }
    this.deckOverlay = null;
  },
  methods: {
    isPointInSelectedArea(lat: number, lon: number) {
      // @ts-ignore
      const areaKey = window.selectedArea;
      if (!areaKey) return true;
      // @ts-ignore
      const area = window.areas[areaKey];
      if (!area || !area[0] || !area[1]) return true;
      const minLat = Math.min(area[0][0], area[1][0]);
      const maxLat = Math.max(area[0][0], area[1][0]);
      const minLon = Math.min(area[0][1], area[1][1]);
      const maxLon = Math.max(area[0][1], area[1][1]);
      return Number.isFinite(lat) && Number.isFinite(lon) && lat >= minLat && lat <= maxLat && lon >= minLon && lon <= maxLon;
    },
    createBinaryData(results: KomootPoiResponse[], color: [number, number, number]): KomootPoiPreparedData {
      const visibleResults = results.filter((place) => this.isPointInSelectedArea(Number(place.lat), Number(place.lon)));
      const positions = new Float32Array(visibleResults.length * 2);
      const fillColors = new Uint8Array(visibleResults.length * 4);
      const lineColors = new Uint8Array(visibleResults.length * 4);
      const filterValues = new Float32Array(visibleResults.length);

      visibleResults.forEach((place, index) => {
        positions[index * 2] = Number(place.lon);
        positions[index * 2 + 1] = Number(place.lat);
        fillColors.set([color[0], color[1], color[2], 51], index * 4);
        lineColors.set([color[0], color[1], color[2], 255], index * 4);
        filterValues[index] = place.is_active ? 1 : 0;
      });

      return {
        results: visibleResults,
        data: {
          length: visibleResults.length,
          attributes: {
            getPosition: { value: positions, size: 2 },
            getFillColor: { value: fillColors, size: 4 },
            getLineColor: { value: lineColors, size: 4 },
            getFilterValue: { value: filterValues, size: 1 },
          },
        },
      };
    },
    ensureDeckOverlay() {
      if (!window.mainmap) return;
      if (!this.deckOverlay) {
        this.deckOverlay = new DeckOverlay({ layers: [] });
        window.mainmap.addLayer(this.deckOverlay);
      }
      if (!this.mapMouseMoveHandler) {
        this.mapMouseMoveHandler = (event: L.LeafletMouseEvent) => this.onMapMouseMove(event);
        window.mainmap.on('mousemove', this.mapMouseMoveHandler);
      }
      if (!this.mapMouseOutHandler) {
        this.mapMouseOutHandler = () => this.onMapMouseOut();
        window.mainmap.on('mouseout', this.mapMouseOutHandler);
      }
      if (!this.mapClickHandler) {
        this.mapClickHandler = (event: L.LeafletMouseEvent) => this.onMapClick(event);
        window.mainmap.on('click', this.mapClickHandler);
      }
    },
    getPoiAt(lat: number, lon: number): { key: string; place: KomootPoiResponse } | null {
      if (this.checkedAll && this.highlightLayer) {
        const index = this.highlightLayer.pick(lat, lon);
        if (index !== null && this.highlightLayerResults[index]) {
          return { key: `highlight-${index}`, place: this.highlightLayerResults[index] };
        }
      }
      if (this.checkedAllOther && this.otherLayer) {
        const index = this.otherLayer.pick(lat, lon);
        if (index !== null && this.otherLayerResults[index]) {
          return { key: `other-${index}`, place: this.otherLayerResults[index] };
        }
      }
      return null;
    },
    onMapMouseMove(event: L.LeafletMouseEvent) {
      const { lat, lng } = event.latlng;
      const poi = this.getPoiAt(lat, lng);
      window.mainmap.getContainer().classList.toggle('komootpoi-pointer-cursor', poi !== null);
      if (poi?.key === this.hoveredPoiKey) return;
      this.clearHoveredWaypoints();
      if (!poi?.place.waypoints) return;

      try {
        const waypoints = JSON.parse(poi.place.waypoints) as L.LatLngExpression[][];
        for (const segment of waypoints) {
          const polyline = new L.Polyline(segment, { color: 'red', weight: 5, opacity: 1, smoothFactor: 1 });
          polyline.addTo(window.mainmap);
          polyline.bringToFront();
          this.hoveredWaypoints.push(polyline);
        }
        this.hoveredPoiKey = poi.key;
      } catch {
        this.hoveredPoiKey = poi.key;
      }
    },
    onMapMouseOut() {
      this.clearHoveredWaypoints();
      if (window.mainmap) window.mainmap.getContainer().classList.remove('komootpoi-pointer-cursor');
    },
    clearHoveredWaypoints() {
      if (window.mainmap) {
        for (const polyline of this.hoveredWaypoints) polyline.removeFrom(window.mainmap);
      }
      this.hoveredWaypoints = [];
      this.hoveredPoiKey = null;
    },
    onMapClick(event: L.LeafletMouseEvent) {
      const poi = this.getPoiAt(event.latlng.lat, event.latlng.lng);
      if (!poi) return;
      const place = this.createPopupPlace(poi.place);
      window.mainmap.openPopup(this.createPopupContent(place), [place.lat, place.lon]);
    },
    createPopupPlace(responsePlace: KomootPoiResponse): KomootPoiPopupPlace {
      return {
        id: responsePlace.id,
        name: responsePlace.name,
        lat: this.roundNumber(Number(responsePlace.lat), 6),
        lon: this.roundNumber(Number(responsePlace.lon), 6),
        photos: (responsePlace.photos || []).map((photoName, index) => new Photo(
          index,
          'Photo',
          '',
          photoName,
          null,
          `${window.mediaHost}komoot_poi/${photoName}.jpg`,
          `${window.mediaHost}komoot_poi/${photoName}.jpg`,
          `${window.mediaHost}komoot_poi/${photoName}_thumb.jpg`,
          false,
          1,
          false,
        )),
      };
    },
    createPopupContent(place: KomootPoiPopupPlace): HTMLElement {
      const content = document.createElement('div');
      content.style.cssText = 'min-width: 200px; max-width: 280px; box-sizing: border-box; overflow-wrap: anywhere;';
      const header = document.createElement('div');
      header.style.cssText = 'margin-bottom: 5px; overflow-wrap: anywhere;';
      const title = document.createElement('b');
      title.textContent = place.name || String(place.id);
      const coords = `${place.lat},${place.lon}`;
      const mapLink = document.createElement('a');
      mapLink.href = `https://maps.google.com/maps?q=${coords}`;
      mapLink.target = '_blank';
      mapLink.rel = 'noopener noreferrer';
      if (window.isDesktop) {
        mapLink.textContent = coords;
        header.append(document.createTextNode(`${place.id} `), title, document.createTextNode(' '), mapLink);
      } else {
        const locationIcon = document.createElement('img');
        locationIcon.src = this.icons.locationBlack;
        locationIcon.style.height = '12px';
        mapLink.append(locationIcon);
        header.append(title, document.createTextNode(' '), mapLink);
      }
      content.append(header);

      if (place.photos.length) {
        let photoIndex = 0;
        const image = document.createElement('img');
        image.className = window.isDesktop ? 'popupimgbig' : 'popupimgsmall';
        image.style.cssText = 'max-width: 100%; cursor: pointer;';
        image.addEventListener('click', (event) => this.openPhotoGallery(place, photoIndex, event));
        image.addEventListener('load', () => (window.mainmap as L.Map & { _popup?: L.Popup })._popup?.update());
        const photoArea = document.createElement('div');
        photoArea.style.textAlign = 'center';
        const setPhoto = async () => {
          const photo = place.photos[photoIndex];
          await photo.generate_image_thumb_cache();
          image.src = photo.image_thumb_cache;
        };

        if (place.photos.length > 1) {
          const table = document.createElement('table');
          table.style.maxWidth = '100%';
          const row = document.createElement('tr');
          const previousCell = document.createElement('td');
          const imageCell = document.createElement('td');
          const nextCell = document.createElement('td');
          const previousImage = document.createElement('img');
          const nextImage = document.createElement('img');
          previousImage.style.cssText = 'width: 10px; height: 20px;';
          nextImage.style.cssText = 'width: 10px; height: 20px;';
          const updateArrows = () => {
            previousImage.src = photoIndex === 0 ? this.icons.arrowLeftDisabled : this.icons.arrowLeft;
            previousImage.style.cursor = photoIndex === 0 ? 'arrow' : 'pointer';
            nextImage.src = photoIndex === place.photos.length - 1 ? this.icons.arrowRightDisabled : this.icons.arrowRight;
            nextImage.style.cursor = photoIndex === place.photos.length - 1 ? 'arrow' : 'pointer';
          };
          previousImage.addEventListener('click', () => {
            if (photoIndex > 0) {
              photoIndex -= 1;
              updateArrows();
              void setPhoto();
            }
          });
          nextImage.addEventListener('click', () => {
            if (photoIndex < place.photos.length - 1) {
              photoIndex += 1;
              updateArrows();
              void setPhoto();
            }
          });
          updateArrows();
          previousCell.append(previousImage);
          imageCell.append(image);
          nextCell.append(nextImage);
          row.append(previousCell, imageCell, nextCell);
          table.append(row);
          photoArea.append(table);
        } else {
          photoArea.append(image);
        }
        void setPhoto();
        content.append(photoArea);
      }
      return content;
    },
    async openPhotoGallery(place: KomootPoiPopupPlace, photoIndex: number, event: MouseEvent) {
      const useOriginal = event.shiftKey || event.ctrlKey || event.altKey;
      await Promise.all(place.photos.flatMap((photo) => [
        photo.generate_image_fullhd_cache(),
        photo.generate_image_thumb_cache(),
      ]));
      const photos = place.photos.map((photo) => ({
        src: useOriginal ? photo.image : photo.image_fullhd_cache,
        thumb: photo.image_thumb_cache,
      }));
      const gallery = lightGallery(document.getElementById('map'), {
        dynamic: true,
        dynamicEl: photos,
        plugins: [lgZoom, lgAutoplay],
        showZoomInOutIcons: true,
        slideShowInterval: 3000,
        index: photoIndex,
        allowMediaOverlap: true,
        mobileSettings: { controls: false, showCloseIcon: true, download: false },
      });
      gallery.openGallery();
    },
    getStatusFilterRange(active: boolean, inactive: boolean): readonly [number, number] {
      if (active && inactive) return [0, 1];
      if (active) return [1, 1];
      if (inactive) return [0, 0];
      return [2, 2];
    },
    createLayer(results: KomootPoiResponse[], id: string, color: [number, number, number], active: boolean, inactive: boolean) {
      const preparedData = this.createBinaryData(results, color);
      return {
        results: preparedData.results,
        layer: new MapLasScatterplotLayer({
          id,
          data: preparedData.data,
          extensions: [new DataFilterExtension({ filterSize: 1 })],
          filterRange: this.getStatusFilterRange(active, inactive),
          positionFormat: 'XY',
          _normalize: false,
          getRadius: 8,
          radiusUnits: 'pixels',
          stroked: true,
          getLineWidth: 2,
          lineWidthUnits: 'pixels',
          visible: true,
          pickable: false,
        }),
      };
    },
    createHighlightLayer() {
      if (!this.komootPoiResult) return;
      const result = this.createLayer(this.komootPoiResult.filter(isHighlightPoi), 'komoot-highlight-poi', [235, 30, 187], this.active, this.inactive);
      this.highlightLayerResults = result.results;
      this.highlightLayer = result.layer;
    },
    createOtherLayer() {
      if (!this.komootPoiResult) return;
      const result = this.createLayer(this.komootPoiResult.filter((place) => !isHighlightPoi(place)), 'komoot-other-poi', [255, 255, 0], this.activeOther, this.inactiveOther);
      this.otherLayerResults = result.results;
      this.otherLayer = result.layer;
    },
    updateDeckLayers() {
      this.ensureDeckOverlay();
      if (!this.deckOverlay) return;
      this.highlightLayer?.setVisible(this.checkedAll);
      this.otherLayer?.setVisible(this.checkedAllOther);
      const layers = [this.highlightLayer, this.otherLayer]
        .filter((layer): layer is MapLasScatterplotLayer => layer !== null)
        .map((layer) => layer.getLayer());
      this.deckOverlay.setProps({ layers });
    },
    onHighlightVisibilityChanged() {
      if (this.checkedAll && !this.highlightLayer) this.createHighlightLayer();
      if (!this.checkedAll && this.hoveredPoiKey?.startsWith('highlight-')) this.clearHoveredWaypoints();
      this.updateDeckLayers();
    },
    onOtherVisibilityChanged() {
      if (this.checkedAllOther && !this.otherLayer) this.createOtherLayer();
      if (!this.checkedAllOther && this.hoveredPoiKey?.startsWith('other-')) this.clearHoveredWaypoints();
      this.updateDeckLayers();
    },
    onHighlightFilterChanged() {
      if (!this.highlightLayer && this.checkedAll) this.createHighlightLayer();
      else this.highlightLayer?.setFilterRange(this.getStatusFilterRange(this.active, this.inactive));
      this.updateDeckLayers();
    },
    onOtherFilterChanged() {
      if (!this.otherLayer && this.checkedAllOther) this.createOtherLayer();
      else this.otherLayer?.setFilterRange(this.getStatusFilterRange(this.activeOther, this.inactiveOther));
      this.updateDeckLayers();
    },
    onSelectedAreaChanged() {
      this.clearHoveredWaypoints();
      if (this.checkedAll && this.komootPoiResult) {
        this.createHighlightLayer();
      } else {
        this.highlightLayer = null;
        this.highlightLayerResults = [];
      }
      if (this.checkedAllOther && this.komootPoiResult) {
        this.createOtherLayer();
      } else {
        this.otherLayer = null;
        this.otherLayerResults = [];
      }
      if (this.checkedAll || this.checkedAllOther) this.updateDeckLayers();
    },
    downloadPlacesKomootPoi() {
      this.loading = true;
      const endPoint = window.apiHost + 'api/stringfield/komootpoi/';
      this.downloadUrlWithCache(endPoint, 'komootpoi', 'komootpoi.txt', null, true).then((response) => {
        this.komootPoiResult = response[1].results;
        this.loaded = true;
        if (response[0] == DownloadStatus.cache_fresh) {
          this.createAlert(AlertStatus.success, this.$t('placesLoaded', [response[1].results.length]).toString(), 2000);
        }
        if (response[0] == DownloadStatus.api_fresh) {
          this.createAlert(AlertStatus.success, this.$t('placesDownloaded', [response[1].results.length]).toString(), 2000);
        }
        if (response[0] == DownloadStatus.cache_last) {
          this.createAlert(AlertStatus.warning, this.$t('placesLoaded', [response[1].results.length]).toString(), 2000);
        }
        if (this.checkedAll) this.createHighlightLayer();
        if (this.checkedAllOther) this.createOtherLayer();
        this.updateDeckLayers();
      }).catch(() => {
        this.createAlert(AlertStatus.danger, this.$t('placesError').toString(), 2000);
      }).finally(() => {
        this.loading = false;
      });
    },
  },
  watch: {
    checkedAll() {
      this.onHighlightVisibilityChanged();
    },
    checkedAllOther() {
      this.onOtherVisibilityChanged();
    },
    active() {
      this.onHighlightFilterChanged();
    },
    inactive() {
      this.onHighlightFilterChanged();
    },
    activeOther() {
      this.onOtherFilterChanged();
    },
    inactiveOther() {
      this.onOtherFilterChanged();
    },
  },
});
</script>